import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {CompanyDataService} from '@services/company-data.service';
import {CompanyService} from '@services/company.service';
import {IBodaccAlert} from '@models/workspace.interface';
import {ICompanyDetails} from '@models/company.interface';
import {NgClass} from '@angular/common';
import {CheckCircle2, LucideAngularModule, RefreshCw} from '@shared/components/icons/google-icons';
import {NotificationService} from '@services/frontend/notification.service';

interface ObligationRow {
  label: string;
  status: 'ok' | 'warning' | 'danger' | 'unknown';
  detail: string;
}

function isDepotComptes(a: IBodaccAlert): boolean {
  const t = `${a.familleAvis || ''} ${a.typeAvis || ''} ${a.description || ''}`.toLowerCase();
  return t.includes('dépôt') && t.includes('compte');
}

function fiscalYearFromParution(dateParution: string | null): number | null {
  if (!dateParution) {
    return null;
  }
  const d = new Date(dateParution);
  if (isNaN(d.getTime())) {
    return null;
  }
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  return month >= 8 ? year - 1 : year - 2;
}

@Component({
  selector: 'app-company-alertes',
  imports: [NgClass, LucideAngularModule],
  templateUrl: './company-alertes.html',
})
export class CompanyAlertes implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly companyService = inject(CompanyService);
  private readonly companyData = inject(CompanyDataService);
  private readonly notificationService = inject(NotificationService);

  alerts = signal<IBodaccAlert[]>([]);
  details = signal<ICompanyDetails | null>(null);
  syncing = signal(false);
  private siret = '';

  obligations = computed<ObligationRow[]>(() => {
    const alerts = this.alerts();
    const d = this.details();
    const currentYear = new Date().getFullYear();
    let creationYear: number | null = null;
    if (d?.dateCreation) {
      const dc = new Date(d.dateCreation as unknown as string);
      if (!isNaN(dc.getTime())) {
        creationYear = dc.getFullYear();
      }
    }
    const filedYears = new Map<number, string>();
    const deposits = alerts.filter(isDepotComptes).sort((a, b) => (b.dateParution || '').localeCompare(a.dateParution || ''));
    for (const alert of deposits) {
      const maxFY = fiscalYearFromParution(alert.dateParution);
      if (!maxFY) {
        continue;
      }
      for (let fy = maxFY; fy >= maxFY - 10; fy--) {
        if (!filedYears.has(fy)) {
          filedYears.set(fy, alert.dateParution || '');
          break;
        }
      }
    }
    const rows: ObligationRow[] = [];
    for (let fiscalYear = currentYear - 3; fiscalYear < currentYear; fiscalYear++) {
      if (creationYear && fiscalYear <= creationYear) {
        continue;
      }
      const filed = filedYears.has(fiscalYear);
      const deadlineMonth = 7;
      const now = new Date();
      const overdue =
        !filed &&
        (now.getFullYear() > fiscalYear ||
          (now.getFullYear() === fiscalYear && now.getMonth() + 1 > deadlineMonth));
      rows.push({
        label: `Dépôt des comptes ${fiscalYear}`,
        status: filed ? 'ok' : overdue ? 'danger' : 'warning',
        detail: filed
          ? `Publication repérée (${filedYears.get(fiscalYear)?.slice(0, 10) || '—'})`
          : overdue
            ? 'Aucun dépôt repéré dans les annonces — à vérifier'
            : 'Échéance habituelle fin juillet — surveillance en cours',
      });
    }
    return rows;
  });

  protected readonly RefreshCw = RefreshCw;
  protected readonly CheckCircle2 = CheckCircle2;

  ngOnInit() {
    this.siret = this.route.parent!.snapshot.paramMap.get('siret')!;
    this.companyData.load(this.siret).subscribe(c => this.details.set(c.company.companyDetailResponse));
    this.loadAlerts();
  }

  loadAlerts() {
    this.companyService.getBodaccAlerts(this.siret).subscribe({
      next: a => this.alerts.set(a),
      error: () => {},
    });
  }

  sync() {
    this.syncing.set(true);
    this.companyService.syncBodacc().subscribe({
      next: () => {
        this.syncing.set(false);
        this.notificationService.success('Données à jour', 2000);
        this.loadAlerts();
      },
      error: () => {
        this.syncing.set(false);
        this.notificationService.error('Synchronisation impossible', 3000);
      },
    });
  }

  statusClass(s: ObligationRow['status']): string {
    switch (s) {
      case 'ok':
        return 'text-green-700 bg-green-50 border-green-100';
      case 'danger':
        return 'text-red-700 bg-red-50 border-red-100';
      case 'warning':
        return 'text-amber-700 bg-amber-50 border-amber-100';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-100';
    }
  }
}
